import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ClipboardCheck, RefreshCw, Inbox } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { ApprovalCard } from '@/components/approval-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function ApprovalsPage() {
  const { user } = useAuth();

  const {
    data: approvals,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['approvals', 'pending', user?.hospital_id],
    queryFn: () => api.approvals.listPending(user?.hospital_id ?? undefined),
    refetchInterval: 30_000,
  });

  return (
    <div className="px-5 py-5 lg:px-8 lg:py-8 max-w-lg lg:max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">
              Pending
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {approvals?.length ?? 0} awaiting decision
            </p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="rounded-full"
        >
          <RefreshCw
            className={cn(
              'w-4 h-4 text-muted-foreground',
              isRefetching && 'animate-spin',
            )}
          />
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[88px] rounded-2xl" />
          ))}
        </div>
      ) : approvals && approvals.length > 0 ? (
        (() => {
          // Two bands, both oldest-first: cases received within the last hour
          // need instant attention (their decision window is live), and must
          // never be buried under the multi-day backlog that stays visible
          // for as long as GlobeMed keeps a case pending.
          const now = Date.now();
          const byReceived = (a: typeof approvals[number], b: typeof approvals[number]) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          const isFresh = (a: typeof approvals[number]) =>
            now - new Date(a.created_at).getTime() < 60 * 60 * 1000;
          const fresh = approvals.filter(isFresh).sort(byReceived);
          const older = approvals.filter((a) => !isFresh(a)).sort(byReceived);
          const banded = [...fresh, ...older];
          return (
            <div className="space-y-3">
              {banded.map((approval, i) => (
                <div key={approval.id}>
                  {fresh.length > 0 && older.length > 0 && i === fresh.length && (
                    <p className="pt-2 pb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Older — still open on GlobeMed
                    </p>
                  )}
                  <ApprovalCard approval={approval} index={i} />
                </div>
              ))}
            </div>
          );
        })()
      ) : (
        <EmptyState />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mb-4">
        <Inbox className="w-8 h-8 text-primary" />
      </div>
      <h3 className="font-sans font-bold text-foreground text-lg">
        All clear
      </h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-[240px]">
        No pending approvals right now. You&apos;ll be notified when new ones arrive.
      </p>
    </motion.div>
  );
}
